import { useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

const OPENED_MODAL_ROUTE = 'openedModalRoute';

interface UseUrlParamModalRouteOptions<T> {
  /** URL param holding the selected item's id, e.g. `programId`. */
  paramName: string;
  items: T[] | undefined;
  getId: (item: T) => string;
}

export interface UseUrlParamModalRouteReturn<T> {
  selected: T | null;
  isModalOpen: boolean;
  open: (id: string, extraParams?: Record<string, string>) => void;
  close: () => void;
}

/**
 * Syncs a detail modal with a URL param so it is deep-linkable and the browser
 * back button closes it. Opening pushes one history entry tagged in location
 * state; closing pops that entry (so Back doesn't reopen the modal), or just
 * rewrites the URL when there is no entry of ours to pop (deep link).
 */
export const useUrlParamModalRoute = <T>({
  paramName,
  items,
  getId,
}: UseUrlParamModalRouteOptions<T>): UseUrlParamModalRouteReturn<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const idFromUrl = searchParams.get(paramName);

  const selected = useMemo(() => {
    if (!idFromUrl || !items) return null;
    return items.find((item) => getId(item) === idFromUrl) ?? null;
  }, [idFromUrl, items, getId]);

  const open = (id: string, extraParams: Record<string, string> = {}) => {
    const params = new URLSearchParams(searchParams);
    params.set(paramName, id);
    Object.entries(extraParams).forEach(([key, value]) => params.set(key, value));
    setSearchParams(params, {
      state: { ...(location.state ?? {}), [OPENED_MODAL_ROUTE]: paramName },
    });
  };

  const close = () => {
    if (location.state?.[OPENED_MODAL_ROUTE] === paramName) {
      navigate(-1);
      return;
    }
    const params = new URLSearchParams(searchParams);
    params.delete(paramName);
    setSearchParams(params, { replace: true });
  };

  return { selected, isModalOpen: !!selected, open, close };
};
